/**
 * Service for managing NotePriority API operations
 */

import { BaseService } from './BaseService';

/**
 * NotePriority Service Class
 */
class NotePriorityService extends BaseService {
    constructor() {
        super('/api/v1/note-priorities');
    }

    /**
     * Validate note priority data
     * @param {Object} data - Note priority data
     */
    validateNotePriorityData(data) {
        if (!data.priorityName || typeof data.priorityName !== 'string') {
            throw new Error('Priority name is required and must be a string');
        }
        
        if (data.priorityName.trim().length === 0) {
            throw new Error('Priority name cannot be empty');
        }
        
        if (data.priorityName.length > 200) {
            throw new Error('Priority name cannot exceed 200 characters');
        }

        if (data.priorityWeight === null || data.priorityWeight === undefined) {
            throw new Error('Priority weight is required');
        }

        if (typeof data.priorityWeight !== 'number') {
            throw new Error('Priority weight must be a number');
        }
        
        if (data.priorityWeight < 0) {
            throw new Error('Priority weight must be greater than or equal to 0');
        }
    }

    /**
     * Get all note priorities
     * @param {string} accessToken - JWT access token
     * @param {string} order - Sort order ('asc' or 'desc')
     * @returns {Promise<Array>} Array of NotePriority objects
     */
    async getAll(accessToken, order = 'desc') {
        try {
            const endpoint = order === 'asc' ? '?order=asc' : '';
            return await this.makeRequest(accessToken, endpoint);
        } catch (error) {
            console.error('Failed to fetch note priorities:', error);
            throw error;
        }
    }

    /**
     * Get note priority by ID - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Get note priority by name
     * @param {string} accessToken - JWT access token
     * @param {string} name - Priority name
     * @returns {Promise<Object>} NotePriority object
     */
    async getByName(accessToken, name) {
        try {
            return await this.makeRequest(accessToken, `/by-name/${encodeURIComponent(name)}`);
        } catch (error) {
            console.error(`Failed to fetch note priority by name ${name}:`, error);
            throw error;
        }
    }

    /**
     * Get highest priority
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} NotePriority object with highest weight
     */
    async getHighest(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/highest');
        } catch (error) {
            console.error('Failed to fetch highest priority:', error);
            throw error;
        }
    }

    /**
     * Get lowest priority
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} NotePriority object with lowest weight
     */
    async getLowest(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/lowest');
        } catch (error) {
            console.error('Failed to fetch lowest priority:', error);
            throw error;
        }
    }

    /**
     * Get priorities with minimum weight
     * @param {string} accessToken - JWT access token
     * @param {number} weight - Minimum weight threshold
     * @returns {Promise<Array>} Array of NotePriority objects
     */
    async getWithMinWeight(accessToken, weight) {
        try {
            return await this.makeRequest(accessToken, `/min-weight/${weight}`);
        } catch (error) {
            console.error(`Failed to fetch priorities with min weight ${weight}:`, error);
            throw error;
        }
    }

    /**
     * Get priorities with maximum weight
     * @param {string} accessToken - JWT access token
     * @param {number} weight - Maximum weight threshold
     * @returns {Promise<Array>} Array of NotePriority objects
     */
    async getWithMaxWeight(accessToken, weight) {
        try {
            return await this.makeRequest(accessToken, `/max-weight/${weight}`);
        } catch (error) {
            console.error(`Failed to fetch priorities with max weight ${weight}:`, error);
            throw error;
        }
    }

    /**
     * Create a new note priority with validation
     * @param {string} accessToken - JWT access token
     * @param {Object} priority - NotePriority object without ID
     * @returns {Promise<Object>} Created NotePriority object
     */
    async create(accessToken, priority) {
        this.validateNotePriorityData(priority);
        return super.create(accessToken, priority);
    }

    /**
     * Update an existing note priority with validation
     * @param {string} accessToken - JWT access token
     * @param {number} id - Priority ID
     * @param {Object} priority - Updated NotePriority object
     * @returns {Promise<Object>} Updated NotePriority object
     */
    async update(accessToken, id, priority) {
        this.validateNotePriorityData(priority);
        return super.update(accessToken, id, priority);
    }

    /**
     * Delete a note priority - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Check if priority name exists
     * @param {string} accessToken - JWT access token
     * @param {string} name - Priority name to check
     * @returns {Promise<boolean>} True if name exists
     */
    async checkNameExists(accessToken, name) {
        try {
            const result = await this.makeRequest(accessToken, `/exists/name/${encodeURIComponent(name)}`);
            return result.exists;
        } catch (error) {
            console.error(`Failed to check if name exists: ${name}:`, error);
            throw error;
        }
    }

    /**
     * Check if priority weight exists
     * @param {string} accessToken - JWT access token
     * @param {number} weight - Priority weight to check
     * @returns {Promise<boolean>} True if weight exists
     */
    async checkWeightExists(accessToken, weight) {
        try {
            const result = await this.makeRequest(accessToken, `/exists/weight/${weight}`);
            return result.exists;
        } catch (error) {
            console.error(`Failed to check if weight exists: ${weight}:`, error);
            throw error;
        }
    }
}

// Export singleton instance
export const notePriorityService = new NotePriorityService();

// Export individual methods for convenience
export const {
    getAll: getAllNotePriorities,
    getById: getNotePriorityById,
    getByName: getNotePriorityByName,
    getHighest: getHighestNotePriority,
    getLowest: getLowestNotePriority,
    getWithMinWeight: getNotePrioritiesWithMinWeight,
    getWithMaxWeight: getNotePrioritiesWithMaxWeight,
    create: createNotePriority,
    update: updateNotePriority,
    delete: deleteNotePriority,
    checkNameExists: checkNotePriorityNameExists,
    checkWeightExists: checkNotePriorityWeightExists
} = notePriorityService;

export default notePriorityService;
